import React, { useState, useEffect, useMemo } from "react";
import { Menu, X, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

import HeroSectionForm from "../../components/dashboard components/forms/HeroSectionForm";
import HomeAboutForm from "../../components/dashboard components/forms/HomeAboutForm";
import ProjectForm from "../../components/dashboard components/forms/ProjectForm";
import ProjectList from "../../components/dashboard components/ProjectList";
import TestimonialForm from "../../components/dashboard components/forms/TestimonialForm";
import FAQForm from "../../components/dashboard components/forms/FAQForm";
import TestimonialList from "../../components/dashboard components/TestimonialList";
import FAQList from "../../components/dashboard components/FAQList";

// Base URLs
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/home`;
const ADMIN_API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const Home = () => {
  const navigate = useNavigate();

  // ===================
  // Layout States
  // ===================
  const [activeSection, setActiveSection] = useState("home");
  const [activeTab, setActiveTab] = useState("hero");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);

  // ===================
  // Tabs Config
  // ===================
  const tabs = [
    { id: "hero", label: "Hero" },
    { id: "about", label: "About" },
    { id: "projects", label: "Project Details" },
    { id: "testimonials", label: "Testimonials" },
    { id: "faq", label: "FAQ" },
  ];

  const activeTabLabel =
    tabs.find((t) => t.id === activeTab)?.label || "Select Section";

  // ===================
  // Data States
  // ===================
  const [heroSlides, setHeroSlides] = useState([]);
  const [aboutData, setAboutData] = useState({
    subtitle: "",
    title: "",
    description: "",
    button: "",
    image: "",
  });

  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialData, setTestimonialData] = useState({});
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  const [faqs, setFaqs] = useState([]);
  const [faqData, setFaqData] = useState({});
  const [editingFAQ, setEditingFAQ] = useState(null);

  // ===================
  // API FETCHERS
  // ===================
  const fetchHeroSlides = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/hero`);
      setHeroSlides(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching hero data:", err);
      setHeroSlides([]);
    }
  };

  const fetchAbout = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about`);
      const d = res.data || {};
      setAboutData({
        subtitle: d.subtitle || "",
        title: d.title || "",
        description: d.description || "",
        button: d.button || "",
        image: d.image || "",
      });
    } catch (err) {
      console.error("Error fetching about:", err);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn("No token found for projects.");
        return;
      }
      const res = await axios.get(`${ADMIN_API_BASE_URL}/admin/project`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjects([]);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/testimonials`);
      setTestimonials(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching testimonials:", err);
      setTestimonials([]);
    }
  };

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/faq`);
      setFaqs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching FAQs:", err);
      setFaqs([]);
    }
  };

  useEffect(() => {
    fetchHeroSlides();
    fetchAbout();
    fetchProjects();
    fetchTestimonials();
    fetchFaqs();
  }, []);

  // ===================
  // GENERIC SAVE HANDLER
  // ===================
  const handleSave = async (sectionName, data) => {
    try {
      const endpointMap = {
        "Hero Slide": "hero",
        About: "about",
        "Project Details": "project",
        Testimonial: "testimonials",
        FAQ: "faq",
      };

      const endpoint = endpointMap[sectionName];
      if (!endpoint) {
        alert("Invalid section name");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        alert("Session expired. Please log in again.");
        return;
      }

      await axios.post(`${API_BASE_URL}/${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert(`${sectionName} saved successfully.`);

      if (sectionName === "Hero Slide") await fetchHeroSlides();
      if (sectionName === "About") await fetchAbout();
      if (sectionName === "Project Details") await fetchProjects();
      if (sectionName === "Testimonial") await fetchTestimonials();
      if (sectionName === "FAQ") await fetchFaqs();
    } catch (err) {
      console.error(`Error saving ${sectionName}:`, err);
      alert("Failed to save. Check console.");
    }
  };

  // ===================
  // PROJECT HANDLERS
  // ===================
  const handleSaveProject = async (formData) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${ADMIN_API_BASE_URL}/admin/update-project`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchProjects();
      setEditingProject(null);
      setShowProjectForm(false);
      alert("Project saved successfully.");
    } catch (err) {
      console.error("Error saving project:", err);
      alert("Failed to save project.");
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/project/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchProjects();
    } catch (err) {
      console.error("Error deleting project:", err);
    }
  };

  const handleTogglePortfolio = async (project) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = project.show_on_portfolio === 1 ? 0 : 1;

      await axios.put(
        `${ADMIN_API_BASE_URL}/admin/portfolio-status`,
        {
          project_id: project.project_id,
          show_on_portfolio: newStatus,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === project.project_id
            ? { ...p, show_on_portfolio: newStatus }
            : p
        )
      );
    } catch (err) {
      console.error("Error updating portfolio status:", err);
    }
  };


  // ===================
// TESTIMONIAL HANDLERS
// ===================
const handleSaveTestimonial = async () => {
  const token = localStorage.getItem("token");
  
  // ✅ Check if editing or creating
  const isEditing = testimonialData.testimonial_id && testimonialData.testimonial_id > 0;
  
  if (isEditing) {
    // ✅ UPDATE - use PUT
    await axios.put(
      `${API_BASE_URL}/testimonials/${testimonialData.testimonial_id}`,
      testimonialData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } else {
    // ✅ CREATE - use POST
    await axios.post(
      `${API_BASE_URL}/testimonials`,
      testimonialData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
  
  await fetchTestimonials();
  setEditingTestimonial(null);
  setTestimonialData({ testimonial_id: 0, name: "", text: "", rating: 0, time: "", image: "" });
  alert("✅ Testimonial saved successfully!");
};

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchTestimonials();
    } catch (err) {
      console.error("Error deleting testimonial:", err);
    }
  };

  // ===================
  // FAQ HANDLERS
  // ===================
  const handleSaveFAQ = async (data) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/faq`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchFaqs();
      setEditingFAQ(null);
      alert("FAQ saved successfully.");
    } catch (err) {
      console.error("Error saving FAQ:", err);
      alert("Failed to save FAQ.");
    }
  };

  const handleDeleteFAQ = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/faq/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchFaqs();
    } catch (err) {
      console.error("Error deleting FAQ:", err);
    }
  };

  // Memoized hero slides
  const memoizedHeroSlides = useMemo(() => heroSlides, [heroSlides]);

  // ===================
  // TAB RENDERER
  // ===================
  const renderActiveTab = () => {
    switch (activeTab) {
      case "hero":
        return (
          <HeroSectionForm
            slides={memoizedHeroSlides}
            onSave={(payload) => handleSave("Hero Slide", payload)}
            onRefetch={fetchHeroSlides}
            apiBase={API_BASE_URL}
          />
        );

      case "about":
        return (
          <HomeAboutForm
            aboutData={aboutData}
            setAboutData={setAboutData}
            onSave={() => handleSave("About", aboutData)}
          />
        );

      case "projects":
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <h2 className="text-lg font-semibold text-blue-600">
          Project Details
        </h2>
      </div>
      <div className="p-5">
        <ProjectList
          projects={projects}
          onTogglePortfolio={handleTogglePortfolio}
        />
      </div>
    </div>
  );

      case "testimonials":
  return editingTestimonial ? (
    <div>
      <button
        onClick={() => {
          setEditingTestimonial(null);
          setTestimonialData({});  // ✅ Reset form data
        }}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg mb-6 transition-all"
      >
        <ArrowLeft size={18} />
        Back to Testimonials
      </button>
      <TestimonialForm
        testimonialData={testimonialData}
        setTestimonialData={setTestimonialData}
        onSave={handleSaveTestimonial}  // ✅ Pass function reference directly
      />
    </div>
  ) : (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <button
          onClick={() => {
            setTestimonialData({
              testimonial_id: 0,
              name: "",
              text: "",
              rating: 0,
              time: "",
              image: "",
            });
            setEditingTestimonial({});
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          + Add Testimonials
        </button>
      </div>
      <div className="p-5">
        <TestimonialList
          testimonials={testimonials}
          onEdit={(item) => {
            setEditingTestimonial(item);
            setTestimonialData({...item});  // ✅ Clone the object
          }}
          onDelete={handleDeleteTestimonial}
        />
      </div>
    </div>
  );
      case "faq":
        return editingFAQ ? (
          <div>
            <button
              onClick={() => setEditingFAQ(null)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg mb-6 transition-all"
            >
              <ArrowLeft size={18} />
              Back to FAQ
            </button>
            <FAQForm
              faqData={faqData}
              setFaqData={setFaqData}
              onSave={() => handleSaveFAQ(faqData)}
            />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">FAQ</h2>
              <button
                onClick={() => {
                  setFaqData({
                    faq_id: 0,
                    question: "",
                    answer: "",
                    sort_order: 0,
                  });
                  setEditingFAQ({});
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              >
                + Add FAQ
              </button>
            </div>
            <div className="p-5">
              <FAQList
                faqs={faqs}
                onEdit={(item) => {
                  setEditingFAQ(item);
                  setFaqData({
                    faq_id: item.faq_id,
                    question: item.question,
                    answer: item.answer,
                    sort_order: item.sort_order || 0,
                  });
                }}
                onDelete={handleDeleteFAQ}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ===================
  // TAB BUTTON COMPONENT (DESKTOP)
  // ===================
  const TabButton = ({ id, label }) => (
    <button
      onClick={() => {
        setActiveTab(id);
        setEditingTestimonial(null);
        setEditingFAQ(null);
      }}
      className={`px-5 py-2 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  // ===================
  // JSX
  // ===================
  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar (desktop) */}
      <div className="hidden lg:block">
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>

      {/* Sidebar (mobile) */}
      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col w-full">
        {/* Header */}
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            className="lg:hidden text-gray-700 hover:text-blue-600"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={28} />
          </button>
          <div className="flex-1 flex justify-center">
            <DashboardHeader title="Home Page Management" backTo="/dashboard" />
          </div>
        </div>

        {/* Tabs with responsive behavior */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          {/* Mobile & Tablet: Hamburger-style selector */}
          <div className="px-4 py-2 flex items-center justify-between lg:hidden">
            <span className="font-semibold text-gray-800 text-sm">
              {activeTabLabel}
            </span>
            <button
              onClick={() => setTabMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <span>Sections</span>
              <Menu size={18} />
            </button>
          </div>

          {tabMenuOpen && (
            <div className="px-4 pb-3 space-y-1 lg:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setEditingTestimonial(null);
                    setEditingFAQ(null);
                    setTabMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Desktop: Horizontal tabs */}
          <div className="hidden lg:flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
            {tabs.map((tab) => (
              <TabButton key={tab.id} id={tab.id} label={tab.label} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white mt-4 sm:mt-6 rounded-xl shadow-md">
          {renderActiveTab()}
        </div>
      </main>
    </div>
  );
};

export default Home;
